import { useState } from "react";
import { Clock, Users, ChefHat, Instagram, Loader2, Heart } from "lucide-react";
import { toast } from "sonner";
import { useAccount } from "@/lib/account";
import { shareRecipeStory } from "@/lib/recipe-image";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { recipeFor } from "@/lib/recipes";
import { whyFor } from "@/lib/faith-customs";

const ShareStory = ({ r, phaseName, why }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const res = await shareRecipeStory(r, phaseName, why);
      if (res === "downloaded") toast("Story image saved", { description: "Open Instagram, tap + then Story, and pick it from your photos." });
    } catch {
      toast.error("Couldn't make the image. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" onClick={go} disabled={busy} data-testid="recipe-share-story-button"
      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2C221E] px-5 py-3 text-sm font-semibold text-[#FBF5EE] transition-colors hover:bg-[#9E4429] disabled:opacity-60">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Instagram className="h-4 w-4" />}Share to Instagram story
    </button>
  );
};

// Heart: members save to their account; visitors get a sign-in prompt
export const HeartButton = ({ id, title, testId, className = "" }) => {
  const { user, toggleFavorite, openAuth } = useAccount();
  const saved = Array.isArray(user?.favorites) && user.favorites.includes(id);
  const click = async () => {
    if (!user) {
      toast("Sign in to save recipes", { description: "Heart your favourites and find them in your Saved list.", action: { label: "Sign in", onClick: () => openAuth("signin") } });
      return;
    }
    try {
      const on = await toggleFavorite(id);
      toast(on ? "Saved to your recipes" : "Removed from saved", { description: title });
    } catch {
      toast.error("Couldn't update your saved recipes. Please try again.");
    }
  };
  return (
    <button type="button" onClick={click} aria-pressed={saved} aria-label={saved ? `Remove ${title} from saved` : `Save ${title}`} data-testid={testId} data-saved={saved}
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#C86D51]/30 transition-[transform,background-color] hover:scale-110 active:scale-95 ${saved ? "bg-[#9E4429] text-[#FBF5EE]" : "bg-transparent text-[#9E4429] hover:bg-[#F4DED3]"} ${className}`}>
      <Heart className={`h-4 w-4 ${saved ? "fill-current" : ""}`} />
    </button>
  );
};

// Simple recipe card for one plate (faith version for members)
export const RecipeCard = ({ phase, body, faith, phaseName, testId = `kitchen-recipe-${phase}` }) => {
  const r = recipeFor(phase, body, faith);
  const id = `${phase}-${body}`;
  const { user } = useAccount();
  return (
    <div className="mt-2 flex items-center gap-2">
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" data-testid={testId}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#C86D51]/30 px-3.5 py-2 text-xs font-semibold text-[#9E4429] transition-colors hover:bg-[#9E4429] hover:text-[#FBF5EE]">
          <ChefHat className="h-3.5 w-3.5" /> Recipe · {r.time}
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-3xl border-[#C86D51]/20 bg-[#FBF5EE] p-7" data-testid="recipe-card">
        <DialogHeader className="text-left">
          <div className="flex items-start justify-between gap-3 pr-6">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9E4429]">{phaseName} plate</p>
              <DialogTitle className="font-serif text-2xl font-normal leading-snug text-[#2C221E]" data-testid="recipe-title">{r.title}</DialogTitle>
            </div>
            <HeartButton id={id} title={r.title} testId="recipe-card-heart" />
          </div>
          <DialogDescription className="flex flex-wrap gap-4 pt-1 text-sm text-[#5C4A42]">
            <span className="flex items-center gap-1.5" data-testid="recipe-time"><Clock className="h-4 w-4" />{r.time}</span>
            <span className="flex items-center gap-1.5"><Users className="h-4 w-4" />Serves {r.serves}</span>
          </DialogDescription>
        </DialogHeader>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A665C]">You'll need</p>
          <ul className="mt-3 space-y-1.5 text-[#2C221E]" data-testid="recipe-ingredients">
            {r.ingredients.map(({ item }) => <li key={item} className="flex gap-2.5 text-sm"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C86D51]" />{item}</li>)}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#7A665C]">Steps</p>
          <ol className="mt-3 space-y-3" data-testid="recipe-steps">
            {r.steps.map((s, i) => (
              <li key={s} className="flex gap-3 text-sm leading-relaxed text-[#3D302B]">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2C221E] text-xs font-semibold text-[#FBF5EE]">{i + 1}</span>{s}
              </li>
            ))}
          </ol>
        </div>
        <p className="rounded-2xl bg-[#F4E6DC] p-4 text-sm text-[#3D302B]"><strong className="text-[#9E4429]">Why it helps:</strong> {whyFor(phase, body)}</p>
        {user && <ShareStory r={r} phaseName={phaseName} why={whyFor(phase, body)} />}
      </DialogContent>
    </Dialog>
    <HeartButton id={id} title={r.title} testId={`recipe-heart-${id}`} />
    </div>
  );
};
